import type { LevelDefinition, LevelProp, PropKind, PropState } from "./types";

export const PROP_KINDS = ["splitKeyboard", "yogaBall", "coffeeBag"] as const;

export const PROP_THROW_DURATION = 0.92;
export const PROP_RELEASE_TIME = 0.5;
export const PROP_EAT_DURATION = 1.42;
export const PROP_EAT_LIFT_END = 0.36;
export const PROP_EAT_DUMP_END = 0.95;
export const PROP_PICKUP_RANGE = 46;
export const PROP_PICKUP_DEPTH = 28;
export const PROP_AIM_RANGE = 360;
export const PROP_AIM_DEPTH = 96;
export const PROP_HIT_RANGE = 40;
export const PROP_HIT_DEPTH = 28;
/** The keyboard flies flat at hip height so it stays low enough to connect for its whole flight. */
export const KEYBOARD_THROW_SPEED = 900;
export const KEYBOARD_THROW_HEIGHT = 34;
export const KEYBOARD_THROW_LIFT = 40;
export const KEYBOARD_GRAVITY_SCALE = 0.25;
export const KEYBOARD_LANE_SPEED = 520;
export const COFFEE_BUFF_DURATION = 8;
export const COFFEE_BUFF_MULTIPLIER = 1.28;
export const COFFEE_BUFF_ATTACK_PACE = 1.18;
export const YOGA_BOUNCE_COUNT = 5;
export const YOGA_BOUNCE_HOP = 0.28;
export const YOGA_HEAD_HEIGHT = 48;

export interface PropDefinition {
  kind: PropKind;
  label: string;
  shout: string;
  damage: number;
  splash: number;
  pierce: number;
  bounce: number;
}

export const PROP_BY_KIND: Record<PropKind, PropDefinition> = {
  splitKeyboard: {
    kind: "splitKeyboard",
    label: "SPLIT KEYBOARD",
    shout: "SPLIT!",
    damage: 6,
    splash: 0,
    pierce: 1,
    bounce: 0
  },
  yogaBall: {
    kind: "yogaBall",
    label: "YOGA BALL",
    shout: "BOUNCE!",
    damage: 2,
    splash: 0,
    pierce: 0,
    bounce: YOGA_BOUNCE_COUNT
  },
  coffeeBag: {
    kind: "coffeeBag",
    label: "TERMINAL BLEND",
    shout: "SUPER!",
    damage: 0,
    splash: 0,
    pierce: 0,
    bounce: 0
  }
};

export function isPropKind(value: string): value is PropKind {
  return (PROP_KINDS as readonly string[]).includes(value);
}

export function isThrowableProp(kind: PropKind): boolean {
  return kind === "splitKeyboard" || kind === "yogaBall";
}

/** Earliest coffees past the first wave, capped per difficulty tier. */
export function selectCoffeeSpawns(level: LevelDefinition, cap: number): LevelProp[] {
  const coffees = (level.props ?? [])
    .filter((prop) => prop.kind === "coffeeBag")
    .sort((first, second) => first.x - second.x);
  const limit = Math.max(0, Math.floor(cap));
  if (limit === 0 || coffees.length === 0) return [];
  const firstWaveX = level.waves[0]?.triggerX ?? 0;
  const earned = coffees.filter((coffee) => coffee.x >= firstWaveX);
  const pool = earned.length > 0 ? earned : coffees;
  return pool.slice(0, limit);
}

export function levelPropsForTier(level: LevelDefinition, cap: number): LevelProp[] {
  const kept = new Set(selectCoffeeSpawns(level, cap));
  return (level.props ?? []).filter((prop) => prop.kind !== "coffeeBag" || kept.has(prop));
}

export function createLevelProps(
  level: LevelDefinition,
  startCounter = 0
): { props: Record<string, PropState>; spawnCounter: number } {
  const props: Record<string, PropState> = {};
  let spawnCounter = startCounter;
  for (const spawn of level.props ?? []) {
    spawnCounter += 1;
    const id = `prop-${spawnCounter}`;
    props[id] = {
      id,
      kind: spawn.kind,
      x: spawn.x,
      y: spawn.y,
      z: 0,
      vx: 0,
      vy: 0,
      vz: 0,
      facing: 1,
      phase: "ground",
      hitIds: [],
      flightTime: 0,
      bouncesRemaining: 0
    };
  }
  return { props, spawnCounter };
}

export function propInPickupRange(
  player: { x: number; y: number },
  prop: { x: number; y: number; phase: PropState["phase"] }
): boolean {
  return (
    prop.phase === "ground" &&
    Math.abs(prop.x - player.x) <= PROP_PICKUP_RANGE &&
    Math.abs(prop.y - player.y) <= PROP_PICKUP_DEPTH
  );
}

export function playerBuffMultiplier(buffTimer = 0): number {
  return buffTimer > 0 ? COFFEE_BUFF_MULTIPLIER : 1;
}
